import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Crown, RefreshCw, ShieldOff, ShieldCheck, Wand2, Activity, DollarSign, Users as UsersIcon } from "lucide-react";

export default function SuperAdmin() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [fin, setFin] = useState(null);
  const [users, setUsers] = useState([]);
  const [sys, setSys] = useState(null);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, f, u, s] = await Promise.all([
        api.get("/super-admin/me"),
        api.get("/super-admin/financials"),
        api.get("/super-admin/users", { params: { q, plan: planFilter || undefined, limit: 50 } }),
        api.get("/super-admin/system-info"),
      ]);
      setMe(m.data); setFin(f.data); setUsers(u.data); setSys(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, planFilter]);

  if (user?.role !== "super_admin") {
    return (
      <div className="bg-white border border-[#0A0A0A] p-8 flex items-center gap-3">
        <ShieldOff className="w-5 h-5" />
        <span className="font-mono-data text-sm">Esta página requiere rol <b>super_admin</b>.</span>
      </div>
    );
  }

  const overridePlan = async (uid) => {
    const plan = prompt("Plan a asignar (free/basic/pro/agency):", "pro");
    if (!plan) return;
    const days = parseInt(prompt("Días de validez:", "30") || "0", 10);
    const reason = prompt("Razón (auditoría):", "comp interno") || "";
    try {
      await api.post(`/super-admin/users/${uid}/override-plan`, { plan, days, reason });
      toast.success("Plan actualizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleSuspend = async (uid, suspended) => {
    const reason = suspended ? prompt("Razón de suspensión:", "") || "" : "";
    try {
      await api.post(`/super-admin/users/${uid}/suspend`, { suspended, reason });
      toast.success(suspended ? "Usuario suspendido" : "Usuario reactivado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const setRole = async (uid, role) => {
    if (!confirm(`¿Confirmar cambio a rol ${role}?`)) return;
    try {
      await api.post(`/super-admin/users/${uid}/role`, { role });
      toast.success("Rol actualizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const runCron = async (job) => {
    try {
      await api.post(`/super-admin/cron/run/${job}`);
      toast.success(`Cron '${job}' ejecutado`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const runHealthCheck = async () => {
    setReportLoading(true);
    try {
      const { data } = await api.post("/super-admin/health-check/run");
      setReport(data);
      toast[data.all_ok ? "success" : "warning"](
        `Health check: ${data.passed}/${data.total} OK · ${data.repaired} reparados`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setReportLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-mark text-[10px] text-[#FFD600]">CIA · CONTROL ROOM</div>
          <h1 className="font-display text-4xl flex items-center gap-3">
            <Crown className="w-8 h-8 text-[#FFD600]" /> Super Admin
          </h1>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-1">
            Vista de plataforma. Solo accesible con rol <b>super_admin</b>.
          </p>
        </div>
        <button onClick={load} className="font-mono-mark text-[10px] border border-[#0A0A0A] px-3 py-2 flex items-center gap-1" data-testid="sa-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> REFRESCAR
        </button>
      </div>

      {/* KPI cards */}
      {fin && (
        <div className="grid sm:grid-cols-4 gap-3">
          <Kpi mark="MRR ESTIMADO" value={`$${fin.mrr_estimated_usd}`} icon={<DollarSign className="w-4 h-4" />} />
          <Kpi mark="TX PAGADAS" value={fin.paid_transactions} icon={<Activity className="w-4 h-4" />} />
          <Kpi mark="REVENUE LIFETIME" value={`$${fin.lifetime_revenue_usd}`} icon={<DollarSign className="w-4 h-4" />} />
          <Kpi mark="USERS PRO/AG" value={(fin.active_users_by_plan?.pro || 0) + (fin.active_users_by_plan?.agency || 0)} icon={<UsersIcon className="w-4 h-4" />} />
        </div>
      )}

      {/* System */}
      {sys && (
        <div className="bg-white border border-[#0A0A0A] p-5">
          <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-3">SISTEMA · COLECCIONES</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono-data text-xs">
            {Object.entries(sys).filter(([k]) => k !== "env").map(([k, v]) => (
              <div key={k} className="border border-[#0A0A0A]/15 p-2">
                <div className="text-[#5C5C5C]">{k}</div>
                <div className="font-display text-xl">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 font-mono-mark text-[9px]">
            {Object.entries(sys.env).map(([k, v]) => (
              <span key={k} className={`px-2 py-1 ${v ? "bg-[#16A34A] text-white" : "bg-[#E5E5E5] text-[#5C5C5C]"}`}>
                {k.toUpperCase()}: {v ? "ON" : "OFF"}
              </span>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => runCron("renewal_upsell")}
              className="font-mono-mark text-[10px] bg-[#FFD600] border-2 border-[#0A0A0A] px-3 py-2 hover-brutal flex items-center gap-1" data-testid="sa-cron-renewal">
              <Wand2 className="w-3 h-3" /> CRON · RENEWAL/UPSELL
            </button>
            <button onClick={() => runCron("publish_due")}
              className="font-mono-mark text-[10px] border border-[#0A0A0A] px-3 py-2 flex items-center gap-1" data-testid="sa-cron-publish">
              <Wand2 className="w-3 h-3" /> CRON · PUBLISH DUE
            </button>
            <button onClick={runHealthCheck} disabled={reportLoading}
              className="font-mono-mark text-[10px] bg-[#0A0A0A] text-[#FFD600] border-2 border-[#0A0A0A] px-3 py-2 hover-brutal flex items-center gap-1" data-testid="sa-health-run">
              <Activity className="w-3 h-3" /> {reportLoading ? "EJECUTANDO..." : "RUN HEALTH CHECK"}
            </button>
          </div>
        </div>
      )}

      {/* Health report */}
      {report && (
        <div className="bg-white border border-[#0A0A0A] p-5" data-testid="sa-health-report">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <span className="font-mono-mark text-[11px]">HEALTH REPORT · {report.started_at?.slice(0,16)} UTC</span>
            <span className={`font-mono-mark text-[10px] px-3 py-1 ${report.all_ok ? "bg-[#16A34A] text-white" : "bg-[#E53935] text-white"}`}>
              {report.passed}/{report.total} OK · {report.repaired} REPARADOS
            </span>
          </div>
          <div className="space-y-1">
            {report.checks.map((c) => (
              <div key={c.name} className="flex items-start gap-3 text-xs font-mono-data" data-testid={`sa-check-${c.name}`}>
                <span>{c.ok ? "✅" : "❌"}{c.repaired ? " 🔧" : ""}</span>
                <span className="font-bold w-44 truncate">{c.name}</span>
                <span className="text-[#5C5C5C] flex-1">{c.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users */}
      <div className="bg-white border border-[#0A0A0A]">
        <div className="px-5 py-3 border-b border-[#0A0A0A] flex flex-wrap items-center gap-3">
          <span className="font-mono-mark text-[11px]">USUARIOS</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar email/nombre"
            className="border border-[#0A0A0A] bg-[#F4F4F0] px-3 py-1.5 font-mono-data text-xs flex-1 min-w-[160px]" data-testid="sa-search" />
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
            className="border border-[#0A0A0A] bg-[#F4F4F0] px-2 py-1.5 font-mono-data text-xs">
            <option value="">Todos los planes</option>
            <option value="free">Inicial</option>
            <option value="basic">Básico</option>
            <option value="pro">Pro</option>
            <option value="agency">Agencia</option>
          </select>
        </div>
        <div className="divide-y divide-[#0A0A0A]/10">
          {users.map((u) => (
            <div key={u.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`sa-user-${u.id.slice(0,8)}`}>
              <div className="min-w-0 flex-1">
                <div className="font-mono-data text-sm truncate">{u.name || "—"} <span className="text-[#5C5C5C]">· {u.email}</span></div>
                <div className="font-mono-mark text-[9px] text-[#5C5C5C]">
                  {u.role?.toUpperCase()} · plan={u.plan} · {u.is_active ? "ACTIVO" : "SUSPENDIDO"}
                  {u.plan_renews_at ? ` · renueva ${u.plan_renews_at.slice(0,10)}` : ""}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                <button onClick={() => overridePlan(u.id)}
                  className="font-mono-mark text-[9px] border border-[#0A0A0A] px-2 py-1.5 hover:bg-[#FFD600]" data-testid={`sa-plan-${u.id.slice(0,8)}`}>PLAN</button>
                <button onClick={() => toggleSuspend(u.id, u.is_active)}
                  className={`font-mono-mark text-[9px] px-2 py-1.5 ${u.is_active ? "border border-[#E53935] text-[#E53935]" : "bg-[#16A34A] text-white border border-[#16A34A]"}`}>
                  {u.is_active ? "SUSPENDER" : "REACTIVAR"}
                </button>
                {u.role !== "super_admin" && (
                  <button onClick={() => setRole(u.id, u.role === "admin" ? "user" : "admin")}
                    className="font-mono-mark text-[9px] border border-[#0A0A0A] px-2 py-1.5 hover:bg-[#0A0A0A] hover:text-white">
                    {u.role === "admin" ? "→USER" : "→ADMIN"}
                  </button>
                )}
                {u.role === "user" && (
                  <button onClick={() => setRole(u.id, "super_admin")} title="Promover a super_admin"
                    className="font-mono-mark text-[9px] bg-[#FFD600] border border-[#0A0A0A] px-2 py-1.5">
                    <ShieldCheck className="inline w-3 h-3 mr-1" />SA
                  </button>
                )}
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="p-6 text-center font-mono-data text-sm text-[#5C5C5C]">Sin resultados.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ mark, value, icon }) {
  return (
    <div className="bg-white border border-[#0A0A0A] p-4">
      <div className="flex items-center justify-between">
        <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{mark}</span>
        <div className="text-[#0A0A0A]">{icon}</div>
      </div>
      <div className="font-display text-3xl mt-2">{value}</div>
    </div>
  );
}
